import { createFileRoute } from '@tanstack/react-router';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBookingByReference, guestName, listingTitle } from '@/lib/data/bookings';
import { computeBookingMoney, inAppPaidLabel } from '@/lib/money/bookingMoney';
import { formatEurCents, formatNad } from '@/lib/money/format';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/bookings/$reference/print')({
  component: BookingPrint,
});

/** A single booking, no chrome (`@media print` in global.css hides the
 * shell) — the second of the task's two print routes (arrivals list +
 * booking). Reception hands this to a guest at check-in or files it as a
 * paper record. */
function BookingPrint() {
  const { reference } = Route.useParams();
  const { businessId, memberships } = useProperty();
  const { data: booking, isLoading } = useBookingByReference(businessId, reference);
  const businessName = memberships.find((m) => m.businessId === businessId)?.businessName ?? '';

  if (isLoading) return <PageLoading compact />;
  if (!booking) return <p className="p-lg text-body text-ink-2">Booking not found.</p>;

  const money = computeBookingMoney({
    totalPrice: booking.total_price,
    depositAmount: booking.deposit_amount,
    balanceRecordedAmountNad: booking.balance_recorded_amount_nad,
    feeStatus: booking.fee_status,
    balanceStatus: booking.balance_status,
  });

  return (
    <div className="p-lg">
      <div className="mb-md flex items-center justify-between">
        <div>
          <h1 className="text-h2 font-extrabold text-ink">Booking {booking.reference_number ?? booking.id}</h1>
          <p className="text-body text-ink-2">{businessName}</p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="print:hidden rounded-button bg-canyon px-md py-xs text-body font-semibold text-white"
        >
          Print
        </button>
      </div>

      <dl className="grid grid-cols-2 gap-y-sm text-body text-ink">
        <dt className="text-ink-2">Guest</dt>
        <dd>{guestName(booking)}</dd>
        <dt className="text-ink-2">Listing</dt>
        <dd>{listingTitle(booking)}</dd>
        <dt className="text-ink-2">Check-in</dt>
        <dd>{booking.check_in ?? '—'}</dd>
        <dt className="text-ink-2">Check-out</dt>
        <dd>{booking.check_out ?? '—'}</dd>
        <dt className="text-ink-2">Party</dt>
        <dd>{booking.guests ?? '—'}</dd>
        <dt className="text-ink-2">Status</dt>
        <dd>{booking.display_status ?? booking.status}</dd>
      </dl>

      <hr className="my-md border-hair" />

      <dl className="grid grid-cols-2 gap-y-sm text-body text-ink">
        <dt className="text-ink-2">Total</dt>
        <dd>{formatNad(money.totalCents / 100)}</dd>
        <dt className="text-ink-2">{inAppPaidLabel(money)}</dt>
        <dd>
          {formatNad(money.feePaidCents / 100)}
          {booking.fee_eur_cents != null ? ` (${formatEurCents(booking.fee_eur_cents)})` : ''}
        </dd>
        <dt className="text-ink-2 font-semibold">Balance due</dt>
        <dd className="font-semibold">{formatNad(money.balanceDueCents / 100)}</dd>
      </dl>

      {booking.notes ? (
        <p className="mt-md text-body text-ink">
          <span className="text-ink-2">Notes: </span>
          {booking.notes}
        </p>
      ) : null}
    </div>
  );
}
